import React from "react";
import {Link} from "react-router-dom";
import {Photo, Session, User} from "../../types/Types";
import {getPhotoDate, truncateString} from "../../util/Util";
import UserTag from "../../users/tag/UserTag";
import LikeButton from "../../tag/likes/LikeButton";
import {V2GlassChip, V2GlassSurface} from "./V2Glass";
import V2PhotoOverlayChip from "./V2PhotoOverlayChip";

interface Props {
    photo: Photo;
    photoUser: User | undefined;
    photoSession?: Session;
    currentUser: User;
    likesMap: Map<string, boolean>;
    likeCount?: number;
    showUser?: boolean;
    locationPlacement?: "top" | "bottom" | "none";
    onLikeCountChange?: (photoId: string, adjustment: number) => void;
    className?: string;
    onImageClick?: (event: React.MouseEvent) => void;
    imageStageClassName?: string;
    imageClassName?: string;
    bodyClassName?: string;
}

function V2PhotoRow(props: Props) {
    const photoTags = props.photo.tags || [];
    const showUser = props.showUser ?? true;
    const locationPlacement = props.locationPlacement ?? "bottom";
    const surfaceClassName = ["v2-hub-photo-row", props.className].filter(Boolean).join(" ");
    const imageStageClassName = ["v2-hub-photo-image-link", props.imageStageClassName].filter(Boolean).join(" ");
    const bodyClassName = ["v2-hub-photo-card-body", props.bodyClassName].filter(Boolean).join(" ");

    const photoImage = (
        <img className={props.imageClassName} src={props.photo.smallSizeUrl} alt={props.photo.locationName} />
    );

    return (
        <V2GlassSurface className={surfaceClassName}>
            <div className="v2-hub-photo-image-wrap">
                {props.onImageClick ? (
                    <div className={imageStageClassName} onClick={props.onImageClick}>
                        {photoImage}
                    </div>
                ) : (
                    <Link className="v2-hub-photo-image-link" to={`/photo/${props.photo.id}`}>
                        {photoImage}
                    </Link>
                )}
                <div className="v2-hub-photo-overlay v2-photo-overlay-top">
                    <div className="v2-hub-photo-user-wrap">
                        {showUser && props.photoUser && (
                            <UserTag
                                user={props.photoUser}
                                visit={undefined}
                                session={props.photoSession}
                                padRight={false}
                                hideLocationDot={false}
                                linksToTab="photos"
                                chipClassName="v2-glass-user-chip"
                                photoOverlay={true}
                            />
                        )}
                        {showUser && !props.photoUser && props.photo.userName && (
                            <Link to={`/profile/${props.photo.userId}/photos`} className="v2-hub-photo-user-link">
                                <V2PhotoOverlayChip className="v2-glass-user-chip">
                                    {truncateString(props.photo.userName)}
                                </V2PhotoOverlayChip>
                            </Link>
                        )}
                        {locationPlacement === "top" && (
                            <Link className="v2-hub-photo-location-link" to={`/location/${props.photo.locationId}/photos`}>
                                <V2PhotoOverlayChip className="v2-glass-location-chip v2-hub-photo-location-chip">
                                    {props.photo.locationName}
                                </V2PhotoOverlayChip>
                            </Link>
                        )}
                    </div>
                    <LikeButton
                        entityId={props.photo.id}
                        likeType="PHOTO"
                        initialLikeCount={props.likeCount ?? props.photo.likeCount}
                        isLiked={props.likesMap.get(props.photo.id) === true}
                        currentUser={props.currentUser}
                        locationId={props.photo.locationId}
                        locationName={props.photo.locationName}
                        onLikeCountChange={props.onLikeCountChange}
                        buttonClassName="v2-hub-photo-like-button v2-photo-overlay-like-button"
                    />
                </div>
                <div className="v2-hub-photo-bottom-overlays">
                    {props.photo.description && (
                        <V2PhotoOverlayChip variant="comment" className="v2-glass-overlay-chip v2-hub-photo-description-chip">
                            {props.photo.description}
                        </V2PhotoOverlayChip>
                    )}
                    {locationPlacement === "bottom" && (
                        <Link className="v2-hub-photo-location-link" to={`/location/${props.photo.locationId}/photos`}>
                            <V2PhotoOverlayChip className="v2-glass-location-chip v2-hub-photo-location-chip">
                                {props.photo.locationName}
                            </V2PhotoOverlayChip>
                        </Link>
                    )}
                </div>
            </div>
            <div className={bodyClassName}>
                <div className="v2-hub-photo-detail-row">
                    {photoTags.length > 0 && (
                        <div className="v2-hub-photo-tags">
                            {photoTags.map(tag => (
                                <Link className="v2-hub-photo-tag-link" to={`/photos/${encodeURIComponent(tag)}`} key={`${props.photo.id}-${tag}`}>
                                    <V2GlassChip className="v2-hub-photo-tag">{tag}</V2GlassChip>
                                </Link>
                            ))}
                        </div>
                    )}
                    <V2GlassChip className="v2-hub-photo-date-chip">
                        {getPhotoDate(props.photo.createdAt)}
                    </V2GlassChip>
                </div>
            </div>
        </V2GlassSurface>
    );
}

export default V2PhotoRow;
